import React from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertTriangle } from "lucide-react";
import { PAYOUT_SETUP_ANCHOR } from "../payments/payoutSetupAnchor";

/**
 * Where affiliate money is actually collected — and whether it can be.
 *
 * THERE IS NO AFFILIATE PAYOUT ENDPOINT (contract §6). Affiliate earnings are ordinary
 * wallet money: they sum into the same buckets as sales and leave through the same
 * step-up-gated withdrawal rail. So this panel is a SIGNPOST to the Gateway Wallet, always,
 * and never a second withdrawal button that could disagree with the first one.
 *
 * `destinationReady` is the server reporting `connect_readiness`'s own predicate — the very
 * one `gateway/payouts.py` refuses a withdrawal on. It is stated here rather than discovered
 * at the wallet, because an affiliate accruing money for weeks against a destination that
 * was never connected has no way to find that out otherwise.
 *
 * The links are RELATIVE (`../payments`): the practitioner console is mounted at
 * `/admin/*` on a platform host and at `/t/{slug}/admin/*` through `tenant/TenantSite.jsx`,
 * and an absolute path would leave the tenant-host mount on a dead route.
 */
export default function PayoutDestinationNotice({ destinationReady, t }) {
  return (
    <section
      data-testid="affiliate-payout-notice"
      className={`rounded-2xl border p-5 ${
        destinationReady ? "bg-white border-eh-border-soft" : "bg-amber-50 border-amber-200"
      }`}
    >
      <h2 className="font-display text-lg text-eh-ink flex items-center gap-2">
        {destinationReady
          ? <Wallet className="w-4 h-4 text-eh-muted" />
          : <AlertTriangle className="w-4 h-4 text-amber-600" />}
        {t("aff.payout.title")}
      </h2>
      <p className="text-sm text-eh-muted mt-1">{t("aff.payout.body")}</p>
      {!destinationReady && (
        <p className="text-sm text-amber-800 mt-2" data-testid="affiliate-payout-not-ready">
          {t("aff.payout.notReady")}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="../payments"
          data-testid="affiliate-payout-wallet"
          className="text-xs px-3 py-1.5 rounded-lg border border-eh-border-soft bg-white"
        >
          {t("aff.payout.walletCta")}
        </Link>
        {!destinationReady && (
          <Link
            to={`../payments#${PAYOUT_SETUP_ANCHOR}`}
            data-testid="affiliate-payout-setup"
            className="text-xs px-3 py-1.5 rounded-lg bg-eh-ink text-eh-bg"
          >
            {t("aff.payout.setupCta")}
          </Link>
        )}
      </div>
    </section>
  );
}
